import React from 'react';
import { Settings as SettingsIcon, Bell, Clock, Target, Droplets, BookOpen, Headphones, Utensils } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

export const Settings = ({ settings, setSettings, requestNotificationPermission }) => {
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      updateSetting('notificationsEnabled', true);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-section">
      {/* Notifications */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/20 flex items-center justify-center">
            <Bell className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Notifications</h3>
            <p className="text-xs text-white/50">Get gentle reminders</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <Label className="text-white">Enable Notifications</Label>
              <p className="text-xs text-white/40 mt-1">Receive motivational reminders</p>
            </div>
            {settings.notificationsEnabled ? (
              <Switch
                checked={settings.notificationsEnabled}
                onCheckedChange={(val) => updateSetting('notificationsEnabled', val)}
                data-testid="notifications-toggle"
              />
            ) : (
              <Button
                onClick={handleEnableNotifications}
                className="btn-primary text-sm py-2 px-4"
                data-testid="enable-notifications-btn"
              >
                Enable
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Reminder Times */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Reminder Times</h3>
            <p className="text-xs text-white/50">When to nudge you</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <Label className="text-white">Morning Reminder</Label>
              <p className="text-xs text-white/40 mt-1">Start your day right</p>
            </div>
            <Input
              type="time"
              value={settings.wakeUpReminder || '06:00'}
              onChange={(e) => updateSetting('wakeUpReminder', e.target.value)}
              className="input-futuristic w-32 text-center font-mono"
              data-testid="morning-reminder-input"
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <Label className="text-white">Wind Down Reminder</Label>
              <p className="text-xs text-white/40 mt-1">Check incomplete habits</p>
            </div>
            <Input
              type="time"
              value={settings.windDownReminder || '20:00'}
              onChange={(e) => updateSetting('windDownReminder', e.target.value)}
              className="input-futuristic w-32 text-center font-mono"
              data-testid="winddown-reminder-input"
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <Label className="text-white">Bedtime Reminder</Label>
              <p className="text-xs text-white/40 mt-1">Time for reflection</p>
            </div>
            <Input
              type="time"
              value={settings.bedtimeReminder || '22:00'}
              onChange={(e) => updateSetting('bedtimeReminder', e.target.value)}
              className="input-futuristic w-32 text-center font-mono"
              data-testid="bedtime-reminder-input"
            />
          </div>
        </div>
      </div>

      {/* Goals */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-lime-400/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-lime-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Daily Goals</h3>
            <p className="text-xs text-white/50">Set your targets</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <Droplets className="w-5 h-5 text-cyan-400" />
              <div>
                <Label className="text-white">Water Goal</Label>
                <p className="text-xs text-white/40 mt-1">Ounces per day</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.waterGoalOunces || 128}
                onChange={(e) => updateSetting('waterGoalOunces', parseInt(e.target.value) || 128)}
                className="input-futuristic w-20 text-center font-mono"
                data-testid="water-goal-input"
              />
              <span className="text-white/50 text-sm">oz</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-amber-400" />
              <div>
                <Label className="text-white">Reading Goal</Label>
                <p className="text-xs text-white/40 mt-1">Pages per day</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.readingGoalPages || 10}
                onChange={(e) => updateSetting('readingGoalPages', parseInt(e.target.value) || 10)}
                className="input-futuristic w-20 text-center font-mono"
                data-testid="reading-goal-input"
              />
              <span className="text-white/50 text-sm">pg</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <Headphones className="w-5 h-5 text-purple-400" />
              <div>
                <Label className="text-white">Audible Goal</Label>
                <p className="text-xs text-white/40 mt-1">Minutes per day</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.audibleGoalMinutes || 60}
                onChange={(e) => updateSetting('audibleGoalMinutes', parseInt(e.target.value) || 60)}
                className="input-futuristic w-20 text-center font-mono"
                data-testid="audible-goal-input"
              />
              <span className="text-white/50 text-sm">min</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <Utensils className="w-5 h-5 text-orange-400" />
              <div>
                <Label className="text-white">Calorie Goal</Label>
                <p className="text-xs text-white/40 mt-1">Calories per day</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.calorieGoal || 2000}
                onChange={(e) => updateSetting('calorieGoal', parseInt(e.target.value) || 2000)}
                className="input-futuristic w-24 text-center font-mono"
                data-testid="calorie-goal-input"
              />
              <span className="text-white/50 text-sm">cal</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
